// FFTBG !fight entry advisor — assemble affordable, priority-aware entry commands.
//
// Costs: 100G per human parameter (class / sex / skill / -exclude), max 4 params,
// and class is required once you use 2+. Monsters are a SOLO entry priced by tier
// (200 normal / 500 strong / 800 elite) and take no other params.
//
// Admission priority (from the rules doc): base 30, +10 entering with no params,
// +5 using your current skill bonus, +5 excluding GilgameHeart, −10 per UNCOMMON
// forced skill (or strong monster), −15 per RARE forced skill (or elite monster /
// hidden skill). The +40 snub / +20 sub / recency / recent-admission factors are
// per-user state we can't see, so callers surface those as caveats.
import { STANDARD_CLASSES } from './skillMods.js';

const RARE_HIT = -15;
const UNCOMMON_HIT = -10;
const MONSTER_COST = { elite: 800, strong: 500, normal: 200 };

// -doppelganger: the new unit isn't saved, so Doppelganger keeps the old one to re-create.
export const isDoppel = (s) => cmdToken(s) === 'doppelganger';

// One-token command form (lowercased, spaces stripped): "Time Mage" → "timemage",
// "Move+3" → "move+3".
export function cmdToken(s) {
  return String(s == null ? '' : s).replace(/\s+/g, '').toLowerCase();
}

// The classes you'd earn the class bonus on = the 20 standard classes minus the
// ones in your `classbonus` dump (recently used). Returns token forms.
export function bonusClassTokens(classbonusLines) {
  const excluded = new Set((classbonusLines || []).map(cmdToken));
  return STANDARD_CLASSES.filter((c) => !excluded.has(cmdToken(c)));
}

// Pick the strongest class from a list by an injected rank(token) → number.
export function pickClass(classTokens, rank) {
  let best = '', bestScore = -Infinity;
  for (const c of classTokens || []) {
    const s = rank ? (rank(c) ?? -Infinity) : 0;
    if (s > bestScore) { bestScore = s; best = c; }
  }
  return best;
}

// Build a human entry: its command, gil cost, and deterministic priority.
// forcedSkills/excludes are token ids; rarityOf(token) → 'rare'|'uncommon'|'common'.
export function buildEntry({ className = '', gender = '', forcedSkills = [], excludes = [], usesSkillBonus = false, rarityOf = () => 'common' } = {}) {
  const params = [];
  if (className) params.push(cmdToken(className));
  if (gender) params.push(cmdToken(gender));  // 'male'/'female' — costs 100G, no priority effect
  for (const s of forcedSkills) params.push(cmdToken(s));
  for (const e of excludes) params.push('-' + cmdToken(e));

  let priority = 30;
  if (!params.length) priority += 10;
  if (usesSkillBonus) priority += 5;
  if (excludes.some((e) => cmdToken(e) === 'gilgameheart')) priority += 5;
  for (const s of forcedSkills) {
    const r = rarityOf(s);
    if (r === 'rare') priority += RARE_HIT;
    else if (r === 'uncommon') priority += UNCOMMON_HIT;
  }

  // 100G per parameter, PLUS the GilgameHeart-exclude bribe: excluding it costs an
  // extra 500G (600G total with the param) to buy the +5 priority.
  const cost = params.length * 100 + (excludes.some((e) => cmdToken(e) === 'gilgameheart') ? 500 : 0);
  return {
    command: '!fight' + (params.length ? ' ' + params.join(' ') : ''),
    cost,
    priority,
    paramCount: params.length,
  };
}

// The FFTBG slots that hold exactly ONE pick — Secondary skillset, Support,
// Reaction, Move, Accessory (genTokens category names). Forcing a skill fills
// its slot, so banning ANOTHER skill in that same slot can never change the roll
// — it just burns 100G. (GilgameHeart's bribe is category 'meta', not a slot
// pick, so it's never pruned.)
export const SINGLE_SLOT_CATEGORIES = new Set(['skillset', 'support', 'reaction', 'movement', 'accessory']);

// Supports that fill the SECONDARY slot too when forced. FFTBG's tips: forcing Throw
// Item "guarantees Item as a secondary", Monster Talk guarantees Talk Skill. So
// `!fight lancer throwitem -basicskill` wastes the ban, the way two Move picks would.
export const LOCKS_SECONDARY = { throwitem: 'Item', monstertalk: 'Talk Skill' };

// The single-slot categories a forced skill fills: its own, plus the secondary for
// the LOCKS_SECONDARY supports. [] when nothing slot-filling is forced.
export function forcedSlots(token, category) {
  const slots = SINGLE_SLOT_CATEGORIES.has(category) ? [category] : [];
  if (token && LOCKS_SECONDARY[cmdToken(token)] && !slots.includes('skillset')) slots.push('skillset');
  return slots;
}

// Drop the now-pointless bans once a skill is forced. `forced` is the category (or
// list of categories, see forcedSlots) the forced skill fills; `categoryOf(ban)`
// returns a ban's category, and a ban in a filled single-slot category is removed
// (everything else, incl. meta bribes, is kept). No-op when nothing slot-filling
// is forced.
export function pruneSameSlotBans(bans, forced, categoryOf) {
  const filled = (Array.isArray(forced) ? forced : [forced]).filter((c) => SINGLE_SLOT_CATEGORIES.has(c));
  if (!filled.length) return bans || [];
  return (bans || []).filter((b) => !filled.includes(categoryOf(b)));
}

// Build a solo monster entry, tier = 'normal' | 'strong' | 'elite'.
export function buildMonsterEntry(monsterToken, tier) {
  return {
    command: '!fight ' + cmdToken(monsterToken),
    cost: MONSTER_COST[tier] || MONSTER_COST.normal,
    priority: 30 + (tier === 'elite' ? RARE_HIT : tier === 'strong' ? UNCOMMON_HIT : 0),
    paramCount: 1,
  };
}

// ── "My entry": a remembered, rule-built entry ──────────────────────────────
// (Suggested by Taobakaa on stream: default to the class you force, and build the
// entry from checkboxes so the best combo is one click from copied.)

// FFTBG takes at most four !fight parameters.
export const MAX_PARAMS = 4;

// The skill a "force my best skill" rule picks from ranked candidates
// [{ token, delta, step, isBonus, rarity }] (best first): the first that beats the
// class baseline (delta > 0) and fits the pool. The pools, loosest first:
//   'usable'    any skill you can still force ("cannot use" starts at 5 tildes).
//   'offcd'     off cooldown: no reroll tildes, so no EXP penalty, but any rarity (an
//               uncommon or rare skill still costs priority to force). Taobakaa's ask:
//               the pick for quiet hours, when priority matters less than winning.
//   'nopenalty' no penalty of either kind: no tildes AND no priority cost to force it,
//               i.e. a common skill (callers rate hidden skills rare).
// Your skill bonus is never held back by its tildes (its modifier is a bonus). A
// missing rarity counts as common.
export const RULE_POOLS = ['usable', 'offcd', 'nopenalty'];
export function pickRuleSkill(candidates, pool = 'usable') {
  for (const c of candidates || []) {
    if (!(c.delta > 0)) continue;
    const tilded = c.step > 0 && !c.isBonus;
    if (pool === 'nopenalty') {
      if (tilded || (c.rarity && c.rarity !== 'common')) continue;
    } else if (pool === 'offcd') {
      if (tilded) continue;
    } else if (c.step >= 5 && !c.isBonus) continue;
    return c.token;
  }
  return '';
}

// Fit an entry to the four-parameter limit. Drops, in order: a skill ban (it only
// dodges one bad roll), the gender unless the entry needs it (a quest target), the
// GilgameHeart bribe, then forced skills from the end. The class is never dropped:
// it's required once there are two parameters. Nor is -doppelganger, which keeps the
// new unit from replacing the one Doppelganger would re-create: dropping it would cost
// the player that saved unit. Returns the kept parts plus
// `dropped: [{ kind: 'ban'|'gender'|'bribe'|'skill', value }]`.
export function fitToLimit({ className = '', gender = '', genderRequired = false, forcedSkills = [], excludes = [] } = {}) {
  let g = gender;
  const forced = [...forcedSkills], ex = [...excludes], dropped = [];
  const isGgh = (e) => cmdToken(e) === 'gilgameheart';
  const count = () => (className ? 1 : 0) + (g ? 1 : 0) + forced.length + ex.length;
  while (count() > MAX_PARAMS) {
    const ban = ex.findIndex((e) => !isGgh(e) && !isDoppel(e));
    if (ban >= 0) { dropped.push({ kind: 'ban', value: ex.splice(ban, 1)[0] }); continue; }
    if (g && !genderRequired) { dropped.push({ kind: 'gender', value: g }); g = ''; continue; }
    const ggh = ex.findIndex(isGgh);
    if (ggh >= 0) { dropped.push({ kind: 'bribe', value: ex.splice(ggh, 1)[0] }); continue; }
    if (forced.length) { dropped.push({ kind: 'skill', value: forced.pop() }); continue; }
    break;
  }
  return { gender: g, forcedSkills: forced, excludes: ex, dropped };
}

// The targets a quest still needs that an entry can deliver, from the goal's
// remaining list (goals.js labels: "Lancer ♂" for a gendered target, otherwise the
// bare class or monster name). `humanClass(name)` → that human class's token, or ''
// if it isn't one; `monsterToken(name)` → the token of a monster you OWN by that
// name, or '' (a monster entry needs the monster). Anything else, like a zodiac
// sign, can't be forced, so it's skipped.
export function questTargets(remaining, { humanClass, monsterToken }) {
  const out = [];
  for (const label of remaining || []) {
    const [, name, sym] = String(label).match(/^(.*?)\s*([♂♀])?$/);
    const gender = sym === '♂' ? 'Male' : sym === '♀' ? 'Female' : '';
    const cls = humanClass(name);
    if (cls) { out.push({ kind: 'class', cls, gender, label }); continue; }
    const tok = gender ? '' : monsterToken(name);
    if (tok) out.push({ kind: 'monster', token: tok, label });
  }
  return out;
}

// The quest target to enter as. A human class comes first: it's cheaper than a
// monster entry and costs no priority. Among those, a class that also earns your
// class-bonus EXP wins, then the strongest by `rank(target)`. An owned monster is
// picked only when no human target is left. null when nothing is enterable.
export function pickQuestTarget(targets, { isBonus = () => false, rank = () => 0 } = {}) {
  const humans = (targets || []).filter((t) => t.kind === 'class');
  const pool = humans.length ? humans : (targets || []);
  let best = null, bestKey = null;
  for (const t of pool) {
    const key = [t.kind === 'class' && isBonus(t.cls) ? 1 : 0, rank(t) ?? -Infinity];
    if (!best || key[0] > bestKey[0] || (key[0] === bestKey[0] && key[1] > bestKey[1])) { best = t; bestKey = key; }
  }
  return best;
}

// ── 🐉 Monster entries ──────────────────────────────────────────────────────
// Strong and elite monsters share a cooldown, one per tier: field any elite and every
// elite is off-limits for a while. The bot's skill-modifier reply lists it as "Elite
// Monster" / "Strong Monster" on the usual tilde ladder (−1 EXP/−3 priority … "cannot
// use"), and in place of those monsters' own lines. The userskills dump has no such
// line, but the step is the MOST tildes on any monster of that tier you own: checked
// against the bot's replies for 28 players on 2026-10-02, it matched every one once
// tournaments finished between the two reads were allowed for. A normal monster has
// only its own tildes. `skills` = [{ token, step }] (parseSkills), `tierOf(token)` →
// 'elite' | 'strong' | 'normal' | '' (not a monster).
export function monsterCooldowns(skills, tierOf) {
  const out = { elite: 0, strong: 0 };
  for (const s of skills || []) {
    const t = tierOf(s.token);
    if (t === 'elite' || t === 'strong') out[t] = Math.max(out[t], s.step || 0);
  }
  return out;
}

// Your owned monsters as solo entries, best first: the ones you can enter as right now
// (off cooldown and affordable) by win rate, then the rest, also by win rate. Each row
// is the buildMonsterEntry entry plus { token, tier, winRate, step, usable, affordable,
// isBonus, freesIn, modText }. The cooldown step's priority cost is folded into
// `priority`; your skill bonus monster gets +5 instead of its own tildes (a strong or
// elite one still waits out its tier's shared cooldown). freesIn = how many more
// tournaments of yours until a "cannot use" (5+ steps) clears to the 4th step.
// Injected: tierOf(token), winRate(token) → % or null, modifier(step, isBonus) →
// fftbgLive's skillModifier, format(mod) → its text. Unranked monsters (no win rate)
// sort last within their group.
export function rankMonsters(skills, { tierOf, winRate, modifier, format = () => '', gil = Infinity, skillBonus = '' } = {}) {
  const cd = monsterCooldowns(skills, tierOf);
  const bonusKey = cmdToken(skillBonus);
  const rows = [];
  for (const s of skills || []) {
    const tier = tierOf(s.token);
    if (!tier) continue;
    const isBonus = !!bonusKey && cmdToken(s.token) === bonusKey;
    const step = tier === 'normal' ? (isBonus ? 0 : s.step || 0) : cd[tier];
    const mod = modifier(step, false);
    const usable = !(mod && mod.cannotUse);
    const e = buildMonsterEntry(s.token, tier);
    if (isBonus) e.priority += 5;
    if (usable && mod && mod.priority) e.priority += mod.priority;
    const wr = winRate(s.token);
    rows.push({
      ...e, token: s.token, tier, winRate: wr == null ? null : wr, step, usable,
      affordable: e.cost <= gil, isBonus, freesIn: usable ? 0 : step - 4,
      modText: mod ? format(mod) : '',
    });
  }
  const ok = (r) => r.usable && r.affordable;
  return rows.sort((a, b) => ok(b) - ok(a) || (b.winRate ?? -1) - (a.winRate ?? -1));
}
